/**
 * Engine-aware SQL formatter with safe fallback.
 *
 * Primary strategy uses `sql-formatter` (real parser) with engine-specific dialects.
 * If formatting fails for any reason, we fall back to a simple regex formatter.
 *
 * `sql-formatter` ships every dialect's grammar (~240 KB minified) and is only
 * ever reached from a user action — the Format SQL command/shortcut, a query
 * preview dialog, the schema-diff "format DDL" toggle. It is therefore pulled in
 * with a cached dynamic `import()` so it lands in its own chunk instead of the
 * eager boot bundle; that is what makes the public API async.
 */
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { formatTSqlBlocks } from './tsqlBlockFormatter';

type SqlFormatterFormat = (typeof import('sql-formatter'))['format'];

type SqlFormatterFn = (sql: string, format: SqlFormatterFormat) => string;

/** Resolved once per session and reused; the loader itself caches the module. */
let sqlFormatterPromise: Promise<SqlFormatterFormat> | null = null;
let loadedFormat: SqlFormatterFormat | null = null;

const loadSqlFormatter = (): Promise<SqlFormatterFormat> => {
  sqlFormatterPromise ??= import('sql-formatter').then((m) => {
    loadedFormat = m.format;
    return m.format;
  });
  return sqlFormatterPromise;
};

/**
 * Preload for SYNCHRONOUS consumers (the electron schema comparer): await this
 * once before entering a sync code path that calls
 * `formatSqlDetailedWith(getLoadedSqlFormatter(), …)`. Never rejects — a load
 * failure just leaves the sync path on the regex fallback, mirroring
 * `formatSqlDetailed`'s own catch.
 */
export const ensureSqlFormatterLoaded = async (): Promise<void> => {
  try {
    await loadSqlFormatter();
  } catch {
    /* sync consumers fall back to formatSqlRegex */
  }
};

/** The loaded `sql-formatter` format function, or null until {@link ensureSqlFormatterLoaded} resolves. */
export const getLoadedSqlFormatter = (): SqlFormatterFormat | null => loadedFormat;

const GO_LINE_REGEX = /^\s*GO(?:\s+\d+)?\s*$/i;

const normalizeGoLine = (line: string): string => {
  const match = line.trim().match(/^GO(?:\s+(\d+))?$/i);
  if (match?.[1]) return `GO ${match[1]}`;
  return 'GO';
};

const splitMsSqlBatches = (sql: string): { readonly batches: readonly string[]; readonly goLines: readonly string[] } => {
  const lines = sql.replace(/\r\n/g, '\n').split('\n');
  const batches: string[] = [];
  const goLines: string[] = [];
  let currentLines: string[] = [];

  for (const line of lines) {
    if (GO_LINE_REGEX.test(line)) {
      batches.push(currentLines.join('\n'));
      goLines.push(line);
      currentLines = [];
      continue;
    }
    currentLines.push(line);
  }

  batches.push(currentLines.join('\n'));
  return { batches, goLines };
};

const formatMsSql = (sql: string, format: SqlFormatterFormat): string => {
  const { batches, goLines } = splitMsSqlBatches(sql);
  const formattedBatches = batches.map((batch) => {
    if (!batch.trim()) return '';
    const base = format(batch, { language: 'tsql', keywordCase: 'upper' }).trim();
    return formatTSqlBlocks(base);
  });

  let result = '';
  for (let i = 0; i < formattedBatches.length; i++) {
    const batch = formattedBatches[i];
    if (batch) {
      if (result) result += '\n\n';
      result += batch;
    }
    const goLine = goLines[i];
    if (goLine) {
      if (result) result += '\n\n';
      result += normalizeGoLine(goLine);
    }
  }

  return result.trim();
};

const formatPgSql = (sql: string, format: SqlFormatterFormat): string => {
  return format(sql, { language: 'postgresql', keywordCase: 'upper' }).trim();
};

const formatSqlite = (sql: string, format: SqlFormatterFormat): string => {
  return format(sql, { language: 'sqlite', keywordCase: 'upper' }).trim();
};

const formatMySql = (sql: string, format: SqlFormatterFormat): string => {
  return format(sql, { language: 'mysql', keywordCase: 'upper' }).trim();
};

const formatOracle = (sql: string, format: SqlFormatterFormat): string => {
  return format(sql, { language: 'plsql', keywordCase: 'upper' }).trim();
};

// KQL is not SQL — sql-formatter has no dialect for it and `supportsFormatting`
// is false for kusto (gated in engineDefinitions.ts), so this is a type-safety
// no-op: return the text unchanged if this map is ever consulted for kusto.
const formatKusto = (sql: string): string => sql;

const formatSqlRegex = (sql: string): string => {
  if (!sql.trim()) return sql;

  // Replace multiple spaces with a single space
  let formatted = sql.replace(/\s+/g, ' ').trim();

  // Add newlines after semicolons
  formatted = formatted.replace(/;/g, ';\n');

  // Handle common SQL keywords for better formatting
  const keywordsToFormat = [
    'SELECT', 'FROM', 'WHERE', 'GROUP BY', 'ORDER BY',
    'HAVING', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN',
    'OUTER JOIN', 'CROSS JOIN', 'UNION', 'INSERT INTO', 'VALUES',
    'UPDATE', 'SET', 'DELETE FROM', 'CREATE TABLE', 'ALTER TABLE',
    'DROP TABLE', 'WITH'
  ];

  // Replace keywords with newline + keyword
  keywordsToFormat.forEach(keyword => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
    formatted = formatted.replace(regex, `\n${keyword}`);
  });

  // Add indentation for JOIN clauses
  formatted = formatted.replace(/\n(LEFT|RIGHT|INNER|OUTER|CROSS)?\s*JOIN/gi, '\n  $&');

  // Indent after WHERE
  formatted = formatted.replace(/\nWHERE\s+/gi, '\nWHERE\n  ');

  // Better AND/OR formatting with indentation
  formatted = formatted.replace(/\b(AND|OR)\b/gi, '\n  $&');

  // Format parentheses for better readability
  let depth = 0;
  let result = '';

  for (let i = 0; i < formatted.length; i++) {
    const char = formatted[i];

    if (char === '(') {
      depth++;
      result += char;
      if (i + 1 < formatted.length && formatted[i + 1] !== ')') {
        result += '\n' + '  '.repeat(depth);
      }
    } else if (char === ')') {
      depth = Math.max(0, depth - 1);
      if (i > 0 && formatted[i - 1] !== '(') {
        result += '\n' + '  '.repeat(depth);
      }
      result += char;
    } else {
      result += char;
    }
  }

  // Remove extra blank lines
  return result.replace(/\n\s*\n/g, '\n').trim();
};

const engineFormatters: Record<DatabaseEngineId, SqlFormatterFn> = {
  mssql: formatMsSql,
  pgsql: formatPgSql,
  sqlite: formatSqlite,
  mysql: formatMySql,
  oracle: formatOracle,
  kusto: formatKusto,
  // Azure Storage has no query language; same identity no-op as kusto.
  azurestorage: formatKusto,
  mock: formatMsSql,
};

export async function formatSql(
  sql: string,
  engineId: DatabaseEngineId | string | undefined = 'mssql',
): Promise<string> {
  return (await formatSqlDetailed(sql, engineId)).formatted;
}

/**
 * Result of {@link formatSqlDetailed}. `fallback` is true when the primary
 * (sql-formatter) path threw and we returned the regex-based output instead —
 * the diff pane shows a warning chip in that case so users know the two
 * sides may not be normalized to the same shape.
 */
export type FormatSqlResult = {
  formatted: string;
  fallback: boolean;
};

export async function formatSqlDetailed(
  sql: string,
  engineId: DatabaseEngineId | string | undefined = 'mssql',
): Promise<FormatSqlResult> {
  let format: SqlFormatterFormat | null = null;
  try {
    format = await loadSqlFormatter();
  } catch {
    format = null;
  }
  return formatSqlDetailedWith(format, sql, engineId);
}

/**
 * Synchronous core shared by the async API above and by synchronous consumers
 * that preloaded via {@link ensureSqlFormatterLoaded}. `format === null` (not
 * yet loaded, or load failed) takes the regex fallback, exactly like the
 * async path's catch.
 */
export function formatSqlDetailedWith(
  format: SqlFormatterFormat | null,
  sql: string,
  engineId: DatabaseEngineId | string | undefined = 'mssql',
): FormatSqlResult {
  if (!sql.trim()) return { formatted: sql, fallback: false };

  const rawEngineId = engineId ?? 'mssql';
  const normalizedEngine: DatabaseEngineId =
    rawEngineId === 'mssql' || rawEngineId === 'pgsql' || rawEngineId === 'sqlite' || rawEngineId === 'mysql' || rawEngineId === 'oracle' || rawEngineId === 'mock'
      ? rawEngineId
      : 'mssql';

  if (format) {
    try {
      return { formatted: engineFormatters[normalizedEngine](sql, format), fallback: false };
    } catch {
      return { formatted: formatSqlRegex(sql), fallback: true };
    }
  }
  return { formatted: formatSqlRegex(sql), fallback: true };
}
